<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import mcpConnectorsAPI from 'dashboard/api/captain/mcpConnectors';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateMcpConnectorDialog from 'dashboard/components-next/captain/pageComponents/mcpConnector/CreateMcpConnectorDialog.vue';
import McpConnectorCard from 'dashboard/components-next/captain/pageComponents/mcpConnector/McpConnectorCard.vue';
import McpConnectorToolsDialog from 'dashboard/components-next/captain/pageComponents/mcpConnector/McpConnectorToolsDialog.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';

const store = useStore();
const { t } = useI18n();
const { shouldShowPaywall } = usePolicy();

const uiFlags = useMapGetter('captainMcpConnectors/getUIFlags');
const connectors = useMapGetter('captainMcpConnectors/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const mcpConnectorsMeta = useMapGetter('captainMcpConnectors/getMeta');

const createDialogRef = ref(null);
const deleteDialogRef = ref(null);
const toolsDialogRef = ref(null);
const selectedConnector = ref(null);

const fetchMcpConnectors = (page = 1) => {
  store.dispatch('captainMcpConnectors/get', { page });
};

const onPageChange = page => fetchMcpConnectors(page);

const openCreateDialog = () => createDialogRef.value.open();

const handleEdit = connector => createDialogRef.value.open(connector);

const handleManageTools = connector => toolsDialogRef.value.open(connector);

const handleDelete = connector => {
  selectedConnector.value = connector;
  nextTick(() => deleteDialogRef.value.dialogRef.open());
};

const handleSync = async connector => {
  try {
    await mcpConnectorsAPI.sync(connector.id);
    useAlert(t('CAPTAIN.MCP_CONNECTORS.SYNC.SUCCESS_MESSAGE'));
    fetchMcpConnectors(mcpConnectorsMeta.value.page);
  } catch (error) {
    useAlert(parseAPIErrorResponse(error));
  }
};

const onDeleteSuccess = () => {
  selectedConnector.value = null;
  // The deleted record is already removed from the store by this point, so
  // an empty page means we just deleted the last connector on it.
  if (connectors.value.length === 0 && mcpConnectorsMeta.value.page > 1) {
    // Go to previous page if current page is now empty
    onPageChange(mcpConnectorsMeta.value.page - 1);
  } else {
    // Refresh current page
    fetchMcpConnectors(mcpConnectorsMeta.value.page);
  }
};

onMounted(() => {
  if (!shouldShowPaywall(FEATURE_FLAGS.CAPTAIN_MCP_CONNECTORS)) {
    fetchMcpConnectors();
  }
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.MCP_CONNECTORS.HEADER')"
    :button-label="$t('CAPTAIN.MCP_CONNECTORS.ADD_NEW')"
    :button-policy="['administrator']"
    :feature-flag="FEATURE_FLAGS.CAPTAIN_MCP_CONNECTORS"
    :total-count="mcpConnectorsMeta.totalCount"
    :current-page="mcpConnectorsMeta.page"
    :show-pagination-footer="!isFetching && !!connectors.length"
    :is-fetching="isFetching"
    :is-empty="!connectors.length"
    :show-know-more="false"
    @update:current-page="onPageChange"
    @click="openCreateDialog"
  >
    <template #paywall>
      <CaptainPaywall feature-prefix="CAPTAIN.MCP_CONNECTORS" />
    </template>

    <template #emptyState>
      <EmptyStateLayout
        :title="$t('CAPTAIN.MCP_CONNECTORS.EMPTY_STATE.TITLE')"
        :subtitle="$t('CAPTAIN.MCP_CONNECTORS.EMPTY_STATE.SUBTITLE')"
        :action-perms="['administrator']"
        :show-backdrop="false"
      >
        <template #actions>
          <Button
            :label="$t('CAPTAIN.MCP_CONNECTORS.ADD_NEW')"
            icon="i-lucide-plus"
            @click="openCreateDialog"
          />
        </template>
      </EmptyStateLayout>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <McpConnectorCard
          v-for="connector in connectors"
          :key="connector.id"
          :connector="connector"
          @edit="handleEdit"
          @delete="handleDelete"
          @sync="handleSync"
          @manage-tools="handleManageTools"
        />
      </div>
    </template>
  </PageLayout>

  <CreateMcpConnectorDialog ref="createDialogRef" />

  <McpConnectorToolsDialog ref="toolsDialogRef" />

  <DeleteDialog
    v-if="selectedConnector"
    ref="deleteDialogRef"
    :entity="selectedConnector"
    type="McpConnectors"
    translation-key="MCP_CONNECTORS"
    @delete-success="onDeleteSuccess"
  />
</template>
